import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Shield, TrendingUp, Users, Building2, Crown, Globe, FileText, Lightbulb } from 'lucide-react';
export function ResearchPage() {
  const researchAreas = [{
    icon: <BookOpen size={28} data-id="element-1695" data-mp="2mh6g9m0t21dy" />,
    title: 'Judgment and Decision-Quality Research',
    color: 'bg-navy',
    description: 'One major area of focus is the study of judgment itself: how people evaluate claims, process tradeoffs, handle ambiguity, respond to pressure, and make decisions under uncertainty. This includes work related to reasoning quality, consequential thinking, narrative vulnerability, emotional reactivity, foresight, and leadership judgment. The goal is to better understand the dimensions of decision-quality that matter most in civic, institutional, and high-responsibility settings.'
  }, {
    icon: <Shield size={28} data-id="element-1696" data-mp="3a7sp3fimvpp0" />,
    title: 'Manipulation Resistance and Information Resilience',
    color: 'bg-teal',
    description: 'SyNeural is exploring how individuals and groups become vulnerable to propaganda, emotional framing, rhetorical deception, identity-driven narratives, and broader forms of informational capture. This area of research includes media literacy, misinformation resilience, persuasion analysis, group pressure dynamics, and the conditions under which people lose or strengthen independent judgment. The aim is to build stronger frameworks for resilience in high-noise information environments.'
  }, {
    icon: <TrendingUp size={28} data-id="element-1697" data-mp="jxenteywi9ss" />,
    title: 'Human Opportunity and Development Pathways',
    color: 'bg-green-soft',
    description: 'A core research priority is the development of models that connect diagnosis to growth. SyNeural is exploring how capability gaps can be translated into meaningful development pathways supported by self-directed tools, educational systems, employers, communities, mentors, and public programs. This includes frameworks for confidence development, self-direction, execution discipline, opportunity access, upward mobility, and long-range readiness.'
  }, {
    icon: <Users size={28} data-id="element-1698" data-mp="3ka63rbit0r30" />,
    title: 'Lifecycle Capability Development',
    color: 'bg-navy-light',
    description: 'SyNeural is also researching how its frameworks can be applied across the full human lifecycle. This includes early development, education, workforce entry, civic adulthood, institutional participation, and leadership progression. The goal is to build a coherent architecture that evolves with responsibility, developmental at the base and increasingly rigorous as the stakes of decision-making rise.'
  }, {
    icon: <Building2 size={28} data-id="element-1699" data-mp="3baj2cpq2rl9y" />,
    title: 'Institutional Readiness and Civil Service Models',
    color: 'bg-navy',
    description: 'Another major area of exploration involves how judgment and readiness can be strengthened inside institutions. This includes research into workforce development, civil-service modernization, role readiness, public-sector leadership pipelines, corruption resistance, systems thinking, and responsibility under pressure. The objective is to create stronger models for institutional capability and public trust.'
  }, {
    icon: <Crown size={28} data-id="element-1700" data-mp="t586ahto55bn" />,
    title: 'Leadership Readiness and Governance Qualification',
    color: 'bg-teal',
    description: 'SyNeural is exploring what a more rigorous framework for leadership readiness could look like in practice. This includes work related to crisis reasoning, escalation control, consequence modeling, institutional responsibility, dissent management, executive judgment, and governance stability. The purpose is to move beyond superficial signals of leadership and toward more structured ways of understanding readiness for high-consequence roles.'
  }, {
    icon: <Globe size={28} data-id="element-1701" data-mp="24l6l72bj2lks" />,
    title: 'Regional and National Capability Mapping',
    color: 'bg-green-soft',
    description: 'At a broader level, SyNeural is researching how aggregate capability patterns might be understood across communities, regions, and nations. This includes work related to youth opportunity bottlenecks, misinformation vulnerability, leadership depth, workforce readiness, local resilience, and the relationship between human capability and institutional strength. This line of work supports the longer-term development of NCJI Atlas and strategic planning applications.'
  }, {
    icon: <Lightbulb size={28} data-id="element-1702" data-mp="uojnb687njnx" />,
    title: 'Pilot Programs and Applied Frameworks',
    color: 'bg-navy-light',
    description: 'In addition to theory and conceptual work, SyNeural is interested in applied pilots and real-world testing environments. These may include educational pilots, workforce-readiness models, civic-capability initiatives, institutional readiness programs, leadership-development frameworks, and public-sector applications. The goal is to test how these ideas perform in practice and refine them through structured implementation.'
  }, {
    icon: <FileText size={28} data-id="element-1703" data-mp="319i32bimt1g0" />,
    title: 'White Papers and Framework Development',
    color: 'bg-navy',
    description: "This section of the platform will also serve as a home for white papers, concept notes, applied frameworks, and future publications. These materials are intended to document SyNeural's evolving thinking and provide a more formal foundation for research partnerships, institutional collaboration, and pilot development over time."
  }];
  return <div className="min-h-screen bg-surface-bg" data-id="element-1704" data-mp="3ozcfirz1gnzq">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1705" data-mp="3szmtorwzikpo">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1706" data-mp="1ttvib956wf8">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1707" data-mp="1xwq7pscnpvaw">
            <span className="w-8 h-px bg-teal" data-id="element-1708" data-mp="1xyb3flapjkac"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1709" data-mp="2cm4hv2o24grc">
              Research
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1710" data-mp="27u4d8wx1kzof"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1711" data-mp="2llrj9ogsdmke">
            Research and Future Work
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1712" data-mp="2hn1wx6drsuom">
            SyNeural's research agenda is focused on a central question: how can
            societies more deliberately develop stronger judgment, greater
            resilience, and better readiness across individuals, institutions,
            and leadership systems?
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1713" data-mp="37u6i4dybvx45">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1714" data-mp="3ctt4bcw4wnp3">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1715" data-mp="2p4hyhgzxg343">
            The company's work sits at the intersection of cognitive assessment,
            human development, civic capability, manipulation resistance,
            institutional performance, and governance readiness.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1716" data-mp="2k4vqblyyhd25">
            This research page outlines the areas SyNeural is actively
            exploring, developing, and refining. Some of this work may take the
            form of white papers, conceptual frameworks, pilot programs, applied
            tools, institutional models, or longer-term research initiatives.
            Together, these efforts are intended to support the continued
            evolution of CJI, NCJI, and the broader SyNeural platform.
          </p>
        </motion.div>
      </section>

      {/* Research Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1717" data-mp="2nl6qia6nfixm">
        <div className="space-y-16" data-id="element-1718" data-mp="2nj2knmp6w9up">
          {researchAreas.map((area, index) => <motion.div key={area.title} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.05
        }} className="flex flex-col lg:flex-row gap-8 items-start" data-id="element-1719" data-mp="2ijpmw6ws5idc" data-mpi={index}>
              {/* Icon & Title */}
              <div className="lg:w-1/3 flex-shrink-0" data-id="element-1720" data-mp="adnrtbtnphaw">
                <div className={`w-16 h-16 rounded-xl ${area.color} flex items-center justify-center text-white mb-4 shadow-lg`} data-id="element-1721" data-mp="fcs102gkzf18">
                  {area.icon}
                </div>
                <h2 className="text-2xl font-heading font-bold text-ink-dark" data-id="element-1722" data-mp="p21tvkj911qe">
                  {area.title}
                </h2>
              </div>

              {/* Description */}
              <div className="lg:w-2/3" data-id="element-1723" data-mp="3sjaa5zybvtj0">
                <div className="bg-white p-6 lg:p-8 rounded-lg border border-line" data-id="element-1724" data-mp="3sk26jczr1vac">
                  <p className="text-ink-muted leading-relaxed" data-id="element-1725" data-mp="1cpq506oxlvh">
                    {area.description}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Our Research Direction */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-1726" data-mp="1hr925b8i95ou">
        <div className="max-w-4xl mx-auto" data-id="element-1727" data-mp="27jkc02gi9equ">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1728" data-mp="27isrpnscz3fv">
            <div className="flex items-center gap-4 mb-8" data-id="element-1729" data-mp="2lcthvt58vi4m">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-1730" data-mp="1om1adjwdmyeg">
                <BookOpen size={24} data-id="element-1731" data-mp="1sn33dizpae7z" />
              </div>
              <h2 className="text-3xl font-heading font-bold" data-id="element-1732" data-mp="1xlo1ncve6nts">
                Our Research Direction
              </h2>
            </div>

            <p className="text-xl leading-relaxed opacity-90 mb-6" data-id="element-1733" data-mp="22lafnoj1o83n">
              SyNeural's research is ultimately aimed at a larger objective:
              building a more coherent and scalable framework for human
              capability, civic resilience, institutional strength, and
              leadership readiness.
            </p>

            <p className="text-lg leading-relaxed opacity-80" data-id="element-1734" data-mp="12e5g79fh5fqs">
              The company is interested not only in measurement, but in the
              deeper question of how societies can deliberately produce stronger
              thinkers, better decision-makers, and more capable leaders over
              time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Research Focus Areas Summary */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1735" data-mp="em6zg9x9as69">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1736" data-mp="9nnd48er3edd">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-1737" data-mp="1z2timxepiuf3">
            Research Focus Areas
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1738" data-mp="3ti0bv05r1eng">
            {[{
            icon: <BookOpen size={20} data-id="element-1739" data-mp="25fhwsik4ia9" />,
            label: 'Judgment Quality'
          }, {
            icon: <Shield size={20} data-id="element-1740" data-mp="3ojp0gkzt2w34" />,
            label: 'Manipulation Resistance'
          }, {
            icon: <TrendingUp size={20} data-id="element-1741" data-mp="g3do3amq58eo" />,
            label: 'Development Pathways'
          }, {
            icon: <Users size={20} data-id="element-1742" data-mp="l1npi2z1l58q" />,
            label: 'Lifecycle Models'
          }, {
            icon: <Building2 size={20} data-id="element-1743" data-mp="72xr8k0hkzis" />,
            label: 'Institutional Readiness'
          }, {
            icon: <Crown size={20} data-id="element-1744" data-mp="2rtg3v8i680hh" />,
            label: 'Leadership Qualification'
          }, {
            icon: <Globe size={20} data-id="element-1745" data-mp="2vv9zh2dieeas" />,
            label: 'Capability Mapping'
          }, {
            icon: <Lightbulb size={20} data-id="element-1746" data-mp="2oa7hhoatxlb3" />,
            label: 'Applied Pilots'
          }].map((item, index) => <motion.div key={item.label} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-surface-muted p-6 rounded-lg border border-line text-center" data-id="element-1747" data-mp="2jeiefazlpma1" data-mpi={index}>
                <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center text-navy mx-auto mb-3 border border-line" data-id="element-1748" data-mp="2aij9nx7m5izx">
                  {item.icon}
                </div>
                <div className="text-sm font-medium text-ink-dark" data-id="element-1749" data-mp="1hun9giiu58ye">
                  {item.label}
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </section>
    </div>;
}